import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner, Badge } from 'react-bootstrap';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faBell, faPlus, faFire, faFlag, faUser, faCheckCircle, faHome } from '@fortawesome/free-solid-svg-icons';
import Api from '../../Api';
import { toast } from 'react-toastify';
import './style.css'; 

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);

  const api = Api();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response: any = await api.dashboard.getStats();
      if (response && response.result) {
        setStats(response.result.users ? response.result : response.result.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <h5 className="mt-3 text-muted fw-bold">Loading Dashboard...</h5>
        </div>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center bg-white p-5 rounded-4 shadow-sm border border-danger">
          <h4 className="text-danger fw-bold mb-3">Failed to load statistics</h4>
          <p className="text-muted">Please try refreshing the page or check the backend server connection.</p>
        </div>
      </Container>
    );
  }

  // Data for Charts
  const userPieData = [
    { name: 'Active Users', value: stats.users.active, color: '#1cc88a' },
    { name: 'Inactive Users', value: stats.users.inactive, color: '#e74a3b' }
  ];

  const propertyPieData = [
    { name: 'Active Properties', value: stats.properties.active, color: '#20c997' },
    { name: 'Unverified Properties', value: stats.properties.total - stats.properties.active, color: '#fd7e14' }
  ];

  const barData = [
    { name: 'Users', Total: stats.users.total, Verified: stats.users.verified },
    { name: 'Properties', Total: stats.properties.total, Verified: stats.properties.verified }
  ];

  return (
    <Container fluid className="dashboard-container py-md-4 px-md-4 p-0">
      <div className="d-none d-md-block p-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
          <div>
            <h2 className="fw-bold text-dark mb-1 d-flex align-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2 text-primary"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
              Interactive Dashboard
            </h2>
            <p className="text-muted mb-0">Live statistics and insights for Cirayedaar.</p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Badge bg="primary" className="px-3 py-2 rounded-pill shadow-sm fs-6 pulse-badge d-flex align-items-center text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
              {stats.engagement.appointmentRequests} Leads
            </Badge>
            <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm fs-6 text-white">
              System Online
            </Badge>
          </div>
        </div>

        <Row className="g-4 mb-4">
          <Col xl={8}>
            <h5 className="fw-bold text-secondary mb-3">Key Metrics Overview</h5>
            <Row>
              <Col lg={4} md={6} className="mb-4">
                <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-primary text-white interactive-card">
                  <Card.Body className="p-4 d-flex flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <p className="mb-1 fw-bold fs-5">Total Users</p>
                        <h2 className="display-6 fw-bold mb-0 count-up">{stats.users.total}</h2>
                      </div>
                      <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={4} md={6} className="mb-4">
                <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-success text-white interactive-card">
                  <Card.Body className="p-4 d-flex flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <p className="mb-1 fw-bold fs-5">Active Users</p>
                        <h2 className="display-6 fw-bold mb-0 count-up">{stats.users.active}</h2>
                      </div>
                      <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4} md={6} className="mb-4">
                <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-info text-white interactive-card">
                  <Card.Body className="p-4 d-flex flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <p className="mb-1 fw-bold fs-5">Verified Profiles</p>
                        <h2 className="display-6 fw-bold mb-0 count-up">{stats.users.verified}</h2>
                      </div>
                      <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4} md={6} className="mb-4">
                <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-purple text-white interactive-card">
                  <Card.Body className="p-4 d-flex flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <p className="mb-1 fw-bold fs-5">Total Properties</p>
                        <h2 className="display-6 fw-bold mb-0 count-up">{stats.properties.total}</h2>
                      </div>
                      <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="9" y1="22" x2="9" y2="22"></line><line x1="15" y1="22" x2="15" y2="22"></line><line x1="9" y1="6" x2="9" y2="6"></line><line x1="15" y1="6" x2="15" y2="6"></line><line x1="9" y1="10" x2="9" y2="10"></line><line x1="15" y1="10" x2="15" y2="10"></line><line x1="9" y1="14" x2="9" y2="14"></line><line x1="15" y1="14" x2="15" y2="14"></line><line x1="9" y1="18" x2="9" y2="18"></line><line x1="15" y1="18" x2="15" y2="18"></line></svg>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={4} md={6} className="mb-4">
                <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-teal text-white interactive-card">
                  <Card.Body className="p-4 d-flex flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <p className="mb-1 fw-bold fs-5">Active Properties</p>
                        <h2 className="display-6 fw-bold mb-0 count-up">{stats.properties.active}</h2>
                      </div>
                      <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4} md={6} className="mb-4">
                <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-orange text-white interactive-card">
                  <Card.Body className="p-4 d-flex flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <p className="mb-1 fw-bold fs-5">Verified Properties</p>
                        <h2 className="display-6 fw-bold mb-0 count-up">{stats.properties.verified}</h2>
                      </div>
                      <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>

          <Col xl={4}>
            <h5 className="fw-bold text-secondary mb-3">User Distribution</h5>
            <Card className="border-0 shadow-sm h-100 chart-card">
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={userPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {userPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4 mb-5">
          <Col xl={6}>
            <h5 className="fw-bold text-secondary mb-3">Verification Comparison</h5>
            <Card className="border-0 shadow-sm chart-card">
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip cursor={{fill: 'rgba(0,0,0,0.05)'}} />
                    <Legend />
                    <Bar dataKey="Total" fill="#4e73df" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Verified" fill="#1cc88a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6}>
            <h5 className="fw-bold text-secondary mb-3">Property Status</h5>
            <Card className="border-0 shadow-sm chart-card">
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={propertyPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {propertyPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Mobile Custom Dashboard View - MyFitnessPal Inspired */}
      <div className="d-block d-md-none pb-5" style={{ backgroundColor: '#f9fafd', padding: '1.5rem', minHeight: '100vh' }}>
        <div className="d-flex justify-content-between align-items-center mb-4 pt-2">
          <div className="d-flex align-items-center">
             <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '45px', height: '45px', fontSize: '1.2rem' }}>
               <FontAwesomeIcon icon={faUser} />
             </div>
             <h3 className="fw-bold mb-0 text-primary" style={{ letterSpacing: '-0.5px' }}>
               cirayedaar 
               <Badge bg="white" text="dark" className="ms-2 border shadow-sm align-middle" style={{ fontSize: '0.6rem' }}>
                 <FontAwesomeIcon icon={faCrown} /> PRO
               </Badge>
             </h3>
          </div>
          <FontAwesomeIcon icon={faBell} className="fs-3 text-dark" />
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="fw-bold mb-0 text-dark" style={{ letterSpacing: '-1px' }}>Today</h1>
          <div className="bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-1 fw-bold" style={{ fontSize: '0.9rem' }}>Edit</div>
        </div>

        {/* Top Main Card (Pie Chart) */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '24px' }}>
          <Card.Body className="p-4">
            <h4 className="fw-bold mb-1 text-dark">Properties</h4>
            <p className="text-muted small mb-4">Total = Active + Unverified</p>
            <div className="d-flex align-items-center justify-content-between pe-2">
              <div style={{ width: '150px', height: '150px', position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={propertyPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                      cornerRadius={10}
                    >
                      {propertyPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                   <h1 className="fw-bold mb-0 text-dark">{stats.properties.total}</h1>
                   <span className="small text-muted fw-bold">Total</span>
                </div>
              </div>
              <div className="ms-3 flex-grow-1">
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <FontAwesomeIcon icon={faFlag} className="text-secondary me-2" />
                    <span className="text-muted small fw-bold">Verified Goal</span>
                  </div>
                  <h4 className="fw-bold mb-0 text-dark">{stats.properties.verified}</h4>
                </div>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-primary me-2" />
                    <span className="text-muted small fw-bold">Active</span>
                  </div>
                  <h4 className="fw-bold mb-0 text-dark">{stats.properties.active}</h4>
                </div>
                <div>
                  <div className="d-flex align-items-center mb-1">
                    <FontAwesomeIcon icon={faFire} className="text-danger me-2" />
                    <span className="text-muted small fw-bold">Leads</span>
                  </div>
                  <h4 className="fw-bold mb-0 text-dark">{stats.engagement.appointmentRequests}</h4>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Grid of Metric Cards */}
        <Row className="g-3">
          <Col xs={6}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '24px' }}>
              <Card.Body className="p-3">
                <h5 className="fw-bold text-dark mb-3">Total Users</h5>
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faUser} className="text-primary me-2 fs-4" />
                  <h3 className="fw-bold mb-0 text-dark">{stats.users.total}</h3>
                </div>
                <p className="text-muted small mt-2 mb-2 fw-bold">Active: {stats.users.active}</p>
                <div className="progress" style={{ height: '8px', borderRadius: '10px' }}>
                  <div className="progress-bar bg-danger" style={{ width: `${(stats.users.active/stats.users.total)*100}%` }}></div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '24px' }}>
              <Card.Body className="p-3 d-flex flex-column justify-content-between">
                <div className="d-flex justify-content-between mb-3">
                  <h5 className="fw-bold text-dark mb-0">Action</h5>
                  <FontAwesomeIcon icon={faPlus} className="text-dark fs-5" />
                </div>
                <div>
                  <div className="d-flex align-items-center mb-3">
                    <FontAwesomeIcon icon={faFire} className="text-warning me-2 fs-5" />
                    <span className="fw-bold mb-0 fs-6 text-dark">{stats.engagement.appointmentRequests} Leads</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-warning me-2 fs-5" />
                    <span className="fw-bold mb-0 fs-6 text-dark">System Up</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '24px' }}>
              <Card.Body className="p-4 d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="fw-bold text-dark mb-1">System Status</h5>
                  <p className="text-muted small mb-0 fw-bold">Last 90 days</p>
                </div>
                <FontAwesomeIcon icon={faPlus} className="text-dark fs-4" />
              </Card.Body>
              <div className="px-4 pb-4">
                 <div className="d-flex justify-content-between small text-muted mb-2 fw-bold">
                   <span>99.9%</span>
                   <span>Uptime</span>
                 </div>
                 <div className="progress" style={{ height: '4px', borderRadius: '10px' }}>
                   <div className="progress-bar bg-dark" style={{ width: '99%' }}></div>
                 </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default Home;
